import { track } from "@/lib/analytics";
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All requests send cookies automatically
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser]           = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading]     = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data.user);
      setWorkspace(data.workspace);
    } catch {
      setUser(null);
      setWorkspace(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    track("login", { method: "password", role: data.user?.role });
    setUser(data.user);
    setWorkspace(data.workspace);
    return data;
  };

  const register = async (payload) => {
    const { data } = await axios.post(`${API}/auth/register`, payload);
    track("sign_up", { method: "password" });
    if (data.workspace?.id) track("trial_start", { plan: data.workspace.subscription_plan || "trial" });
    setUser(data.user);
    setWorkspace(data.workspace);
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    setUser(null);
    setWorkspace(null);
  };

  const refreshWorkspace = async () => {
    try {
      const { data } = await axios.get(`${API}/workspaces/current`);
      setWorkspace(data);
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, workspace, loading, login, register, logout, refreshWorkspace, setWorkspace }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
